/**
 * Admin · tenant profile · Overview — "Referred application".
 *
 * Renders only when the application actually carries a referral code, so an
 * ordinary applicant's Overview is unchanged. When it does render, it sits above
 * Personal Information: whether someone was referred changes how you read the
 * rest of the record, so it belongs before the record, not after it.
 *
 * The card exists to let staff VERIFY a referral before approving the
 * application (programme spec 5.3), which means answering, without leaving the
 * screen:
 *   · which code was used, and when it was entered
 *   · who the referrer is, and whether they are a real, current tenant
 *   · how many referrals they have already landed — the signal that separates a
 *     tenant telling their friends from someone working the programme
 *   · where the EUR 100 stands, and that nothing needs doing by hand
 *
 * Everything comes from admin_booking_referral(), which refuses non-staff
 * outright. Unmasked here on purpose: this is the staff surface, unlike the
 * tenant's own list.
 *
 * Bilingual like the rest of the referral feature: the admin shell carries a
 * DE/EN toggle (AdminUserMenu), so a card that only ever spoke English left an
 * admin on Deutsch reading half a screen in the wrong language. Strings are
 * inline rather than translation rows, matching how the tenant-facing referral
 * surfaces in src/lib/referrals.ts do it.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDate, formatDateTime } from "@/lib/v2-format";
import { chip, uppLabel, cardWrap, whitePanel } from "./tenant-profile-ui";

interface AdminReferral {
  id: string;
  stage: "applied" | "approved" | "signed" | "rewarded";
  code_used: string;
  created_at: string | null;
  approved_at: string | null;
  signed_at: string | null;
  rewarded_at: string | null;
  closed_at: string | null;
  reward_amount_cents: number;
  reward_payment_id: string | null;
  reward_due_date: string | null;
  referrer_user_id: string;
  referrer_name: string | null;
  referrer_email: string | null;
  referrer_room: string | null;
  referrer_successful_count: number;
  /**
   * Earned, but the referrer has no upcoming rent row for the EUR 100 to land
   * on — usually because they have moved out. Computed live by the same
   * predicate the tenant's own dashboard uses, so staff and tenant never see
   * different stories about one referral.
   */
  reward_stalled: boolean;
}

/** Where the money stands, said plainly enough to act on. */
function rewardState(
  r: AdminReferral,
  de: boolean
): {
  label: string;
  style: React.CSSProperties;
} {
  const eur = `€${(r.reward_amount_cents / 100).toFixed(0)}`;
  if (r.stage === "rewarded") {
    return {
      label: de ? `${eur} gutgeschrieben` : `${eur} credited`,
      style: chip("#E6F7EF", "#047857", "#C6EEDB"),
    };
  }
  if (r.stage === "signed") {
    // Stalled is not "waiting", it is "stuck" — amber, because unlike every
    // other state on this card it is the one that needs a human.
    if (r.reward_stalled) {
      return {
        label: de
          ? `${eur} verdient – keine Miete zur Verrechnung`
          : `${eur} earned — no rent to credit`,
        style: chip("#FEF6E7", "#B45309", "#F7E4BD"),
      };
    }
    return {
      label: de
        ? "Verdient – wartet auf eine Mietzeile"
        : "Earned — awaiting a rent row",
      style: chip("#E5FAFD", "#0E7490", "#C4EFF6"),
    };
  }
  return {
    label: de ? `${eur} ausstehend` : `${eur} pending`,
    style: chip("#FEF6E7", "#B45309", "#F7E4BD"),
  };
}

function Cell({
  label,
  children,
  borderRight,
  borderBottom,
}: {
  label: string;
  children: React.ReactNode;
  borderRight?: boolean;
  borderBottom?: boolean;
}) {
  return (
    <div
      style={{
        padding: "14px 20px",
        borderRight: borderRight ? "1px solid #F0F1F3" : undefined,
        borderBottom: borderBottom ? "1px solid #F0F1F3" : undefined,
        minWidth: 0,
      }}
    >
      <div style={uppLabel}>{label}</div>
      <div
        style={{
          marginTop: 4,
          fontSize: 14,
          fontWeight: 700,
          color: "#0F172A",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function ReferralSection({ bookingId }: { bookingId: number | null }) {
  const { language } = useLanguage();
  const de = language === "de";

  const { data: referral } = useQuery<AdminReferral | null>({
    queryKey: ["admin-booking-referral", bookingId],
    queryFn: async () => {
      if (!bookingId) return null;
      const { data, error } = await supabase.rpc("admin_booking_referral", {
        _booking_id: bookingId,
      });
      if (error) throw new Error(error.message);
      // jsonb: the AdminReferral shape, or null.
      return (data as unknown as AdminReferral | null) ?? null;
    },
    enabled: !!bookingId,
    staleTime: 30_000,
  });

  // Not a referred application — say nothing rather than showing an empty card.
  if (!referral) return null;

  const reward = rewardState(referral, de);

  return (
    <div style={cardWrap}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: "#0B7268",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
          >
            <circle cx="18" cy="5" r="2.6" />
            <circle cx="6" cy="12" r="2.6" />
            <circle cx="18" cy="19" r="2.6" />
            <path d="M8.3 10.8 15.7 6.4M8.3 13.2l7.4 4.4" />
          </svg>
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: "-.01em",
            flex: 1,
          }}
        >
          {de ? "Empfohlene Bewerbung" : "Referred application"}
        </h3>
        <span style={reward.style}>{reward.label}</span>
      </div>

      <div
        style={{
          ...whitePanel,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          overflow: "hidden",
        }}
      >
        <Cell
          label={de ? "Empfehlungscode" : "Referral code"}
          borderRight
          borderBottom
        >
          <span
            style={{
              letterSpacing: ".03em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {referral.code_used}
          </span>
        </Cell>

        <Cell label={de ? "Empfohlen von" : "Referred by"} borderBottom>
          {/* The referrer is a tenant in this same admin area — link straight to
              them so verifying "is this a real tenant?" is one click, not a search. */}
          <Link
            to={`/admin/directory?q=${encodeURIComponent(referral.referrer_email ?? referral.referrer_name ?? "")}`}
            style={{ color: "#0B7268", textDecoration: "none" }}
          >
            {referral.referrer_name || referral.referrer_email || "—"}
          </Link>
          {referral.referrer_email && (
            <div
              style={{
                marginTop: 2,
                fontSize: 12.5,
                fontWeight: 500,
                color: "#64748B",
              }}
            >
              {referral.referrer_email}
            </div>
          )}
        </Cell>

        <Cell
          label={de ? "Zimmer des Empfehlenden" : "Referrer's room"}
          borderRight
          borderBottom
        >
          {referral.referrer_room || "—"}
        </Cell>

        <Cell
          label={de ? "Bisherige Empfehlungen" : "Referrer's track record"}
          borderBottom
        >
          {referral.referrer_successful_count === 0
            ? de
              ? "Erste Empfehlung"
              : "First referral"
            : de
              ? `${referral.referrer_successful_count} bisher erfolgreich`
              : `${referral.referrer_successful_count} successful so far`}
        </Cell>

        <Cell label={de ? "Code eingegeben" : "Code entered"} borderRight>
          {referral.created_at
            ? (formatDateTime(referral.created_at) ?? "—")
            : "—"}
        </Cell>

        <Cell label={de ? "Prämie" : "Reward"}>
          {referral.stage === "rewarded" ? (
            referral.reward_due_date ? (
              de ? (
                <>
                  Mit der Miete vom{" "}
                  {formatDate(referral.reward_due_date) ?? "nächsten"}{" "}
                  verrechnet
                </>
              ) : (
                <>
                  Applied to the{" "}
                  {formatDate(referral.reward_due_date) ?? "next"} rent
                </>
              )
            ) : de ? (
              <>Verrechnet</>
            ) : (
              <>Applied</>
            )
          ) : referral.stage === "signed" ? (
            referral.reward_stalled ? (
              de ? (
                <>Verdient – zurzeit keine offene Miete zur Verrechnung</>
              ) : (
                <>Earned — no upcoming rent to credit it against</>
              )
            ) : de ? (
              <>Verdient – wird mit der nächsten Mietzeile verrechnet</>
            ) : (
              <>Earned — lands on their next rent row</>
            )
          ) : de ? (
            <>Noch nicht verdient – Vertrag nicht unterschrieben</>
          ) : (
            <>Not yet earned — contract unsigned</>
          )}
        </Cell>
      </div>

      <p
        style={{
          margin: "14px 2px 0",
          fontSize: 12.5,
          lineHeight: 1.55,
          color: "#64748B",
        }}
      >
        {referral.closed_at ? (
          de ? (
            <>
              Diese Bewerbung ist beendet. Eine bereits gutgeschriebene Prämie
              wird nicht zurückgefordert; eine nicht verdiente wird schlicht nie
              ausgezahlt.
            </>
          ) : (
            <>
              This application is closed. A reward already credited is not
              clawed back; an unearned one simply never pays out.
            </>
          )
        ) : referral.reward_stalled ? (
          /* The one case where "nothing to do here" would be a lie: the
             referral is earned and the automatic path cannot finish it,
             because the referrer has no rent left to discount. */
          de ? (
            <>
              Diese Prämie ist verdient, kann aber nicht automatisch verrechnet
              werden: Für{" "}
              {referral.referrer_name ||
                referral.referrer_email ||
                "den Empfehlenden"}{" "}
              steht keine offene Miete an. Sie bleibt verdient, bis es wieder
              eine gibt – wenn das nicht absehbar ist, braucht es eine
              Entscheidung, wie ausgezahlt wird.
            </>
          ) : (
            <>
              This reward is earned but cannot be credited automatically:{" "}
              {referral.referrer_name ||
                referral.referrer_email ||
                "the referrer"}{" "}
              has no upcoming rent to discount. It stays earned until they do —
              if that is not coming, someone needs to decide how it gets paid.
            </>
          )
        ) : de ? (
          <>
            Hier ist nichts zu tun. Die Gutschrift von €
            {(referral.reward_amount_cents / 100).toFixed(0)} wird automatisch
            verrechnet, sobald der unterschriebene Vertrag erfasst ist – und
            immer nur einmal pro empfohlener Bewerbung.
          </>
        ) : (
          <>
            Nothing to do here. The €
            {(referral.reward_amount_cents / 100).toFixed(0)} credit is applied
            automatically once the signed contract is recorded — and only ever
            once per referred application.
          </>
        )}
      </p>
    </div>
  );
}

export default ReferralSection;
